import { rawLocalStorage } from "@sfc/ui/local-storage";
import { createExternalStore } from "@client/utils/createExternalStore";

// Playback-speed preference shared by every VideoPlayer on the page and
// persisted across sessions. Changing the speed on one video applies to all
// videos — including players mounted later, which would otherwise reset to
// 1x on every new video or clip change (SP-2377).
//
// The allowed range mirrors the settings-menu speed UI (preset rates from
// 0.5x to 3x plus a custom-rate textbox — see SpeedMenuItems). Values
// arriving from outside that UI (an old stored value, the browser's native
// controls) are clamped so every player stays within the range the menu can
// express.

const STORAGE_KEY = "video-playback-rate";

export const MIN_PLAYBACK_RATE = 0.5;
export const MAX_PLAYBACK_RATE = 3;
export const PLAYBACK_RATE_STEP = 0.1;

function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) {
    return 1;
  }

  const clamped = Math.min(
    MAX_PLAYBACK_RATE,
    Math.max(MIN_PLAYBACK_RATE, rate),
  );
  // The speed slider reports values rounded to 2 decimals; normalize other
  // sources the same way so the menu hint never shows float noise like
  // "1.7000000000000002x".
  return Math.round(clamped * 100) / 100;
}

function readStoredRate(): number {
  try {
    const raw = rawLocalStorage.getItem(STORAGE_KEY);

    if (raw == null) {
      return 1;
    }

    return clampRate(Number(raw));
  } catch {
    // localStorage can throw (privacy mode / storage disabled); fall back
    // to normal speed.
    return 1;
  }
}

const store = createExternalStore<number>(readStoredRate());

export function usePlaybackRate(): number {
  return store.useValue();
}

// Non-hook read for code outside the render tree (the watch-segment builder
// samples the rate on each progress observation).
export function getPlaybackRate(): number {
  return store.get();
}

// Wired to the MediaPlayer `rate-change` event, which fires both for user
// changes through the speed slider and for our own programmatic prop sync —
// the latter arrives with the already-stored value and no-ops.
export function setPlaybackRate(rate: number): void {
  const next = clampRate(rate);
  store.set(next);

  try {
    rawLocalStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // Best-effort persistence; the in-memory store still applies app-wide.
  }
}
